import * as tus from "tus-js-client";
import { supabase } from "@/integrations/supabase/client";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;

export interface ResumableUploadOptions {
  bucket: string;
  path: string;
  file: File;
  contentType?: string;
  upsert?: boolean;
  onProgress?: (percent: number, uploadedBytes: number, totalBytes: number) => void;
  signal?: AbortSignal;
}

/**
 * Upload a file to Supabase Storage using the resumable (TUS) protocol.
 *
 * TUS chunks the file into small pieces (default 6 MB) so slow connections and
 * large files (scanned PDFs, video, etc.) don't hit browser or gateway timeouts
 * the way a single `.upload()` fetch does. Chunks resume automatically after
 * network hiccups.
 */
export async function resumableUpload(opts: ResumableUploadOptions): Promise<void> {
  const {
    bucket,
    path,
    file,
    contentType = file.type || "application/octet-stream",
    upsert = false,
    onProgress,
    signal,
  } = opts;

  const { data: sessionData, error: sessionErr } = await supabase.auth.getSession();
  if (sessionErr) throw sessionErr;
  const accessToken = sessionData.session?.access_token;
  if (!accessToken) throw new Error("You must be signed in to upload.");

  return new Promise<void>((resolve, reject) => {
    const upload = new tus.Upload(file, {
      endpoint: `${SUPABASE_URL}/storage/v1/upload/resumable`,
      retryDelays: [0, 1000, 3000, 5000, 10000],
      headers: {
        authorization: `Bearer ${accessToken}`,
        "x-upsert": upsert ? "true" : "false",
      },
      uploadDataDuringCreation: true,
      removeFingerprintOnSuccess: true,
      metadata: {
        bucketName: bucket,
        objectName: path,
        contentType,
        cacheControl: "3600",
      },
      // 6 MB chunks — required by Supabase's TUS endpoint
      chunkSize: 6 * 1024 * 1024,
      onError: (err: any) => {
        // Surface HTTP body when available for a useful message
        const body = err?.originalResponse?.getBody?.();
        const status = err?.originalResponse?.getStatus?.();
        let msg = err?.message || "Upload failed.";
        if (status === 413) {
          msg = "File is larger than the server allows. Please upload a smaller file.";
        } else if (body) {
          try {
            const parsed = JSON.parse(body);
            msg = parsed.message || parsed.error || msg;
          } catch {
            /* keep default */
          }
        }
        reject(new Error(msg));
      },
      onProgress: (bytesUploaded, bytesTotal) => {
        if (onProgress) {
          const pct = bytesTotal ? Math.round((bytesUploaded / bytesTotal) * 100) : 0;
          onProgress(pct, bytesUploaded, bytesTotal);
        }
      },
      onSuccess: () => resolve(),
    });

    if (signal) {
      const onAbort = () => {
        try {
          upload.abort(true);
        } catch {
          /* ignore */
        }
        reject(new Error("Upload cancelled."));
      };
      if (signal.aborted) return onAbort();
      signal.addEventListener("abort", onAbort, { once: true });
    }

    // Resume if there is a previous fingerprint for this file
    upload.findPreviousUploads().then((prev) => {
      if (prev.length > 0) upload.resumeFromPreviousUpload(prev[0]);
      upload.start();
    });
  });
}

/** Largest file a member may upload. Bigger scans time out and help no one. */
export const MAX_UPLOAD_BYTES = 100 * 1024 * 1024;

/** Files at or above this size go up in chunks so slow lines can resume. */
const RESUMABLE_THRESHOLD = 6 * 1024 * 1024;

/** Plain language message shown when a file is over the limit. */
export const FILE_TOO_LARGE_MESSAGE =
  "This file is over 100 MB. Scan at a lower resolution or compress the PDF, then try again.";

/**
 * Upload a file the safe way: small files go in one request, large files go
 * up in chunks that resume after a network hiccup. Same call either way.
 */
export async function uploadFileToStorage(opts: {
  bucket: string;
  path: string;
  file: File;
  upsert?: boolean;
  onProgress?: (percent: number) => void;
}): Promise<void> {
  const { bucket, path, file, upsert = false, onProgress } = opts;
  const contentType = file.type || "application/octet-stream";

  if (file.size >= RESUMABLE_THRESHOLD) {
    await resumableUpload({
      bucket,
      path,
      file,
      contentType,
      upsert,
      onProgress: (pct) => onProgress?.(pct),
    });
    return;
  }

  onProgress?.(0);
  const { error } = await supabase.storage
    .from(bucket)
    .upload(path, file, { contentType, upsert });
  if (error) throw error;
  onProgress?.(100);
}
